/* eslint-disable max-len */
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from 'components/home';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useSnackbar } from 'notistack';
import { ContainerForm } from 'components/shared/container_form';
import { CicaafAPI } from 'api/cicaaf/cicaaf.api';
import { mascaraCpfCnpj } from 'utils/format_cpf';
import { Image, PictureAsPdf } from '@mui/icons-material';
import { mascaraTelefone } from 'utils/format_phone';
import { UserPermissionEnum } from 'enum/userPermission.enum';
import { useAuth } from 'contexts/auth/auth_context';


export const mapSealLabel = (sealCode: string): string => {
  const labels: Record<string, string> = {
    SIM: 'Serviço de Inspeção Municipal – SIM',
    SISE: 'Serviço de Inspeção Estadual – SISE',
    SIF: 'Serviço de Inspeção Federal – SIF',
    SISBI: 'Sistema Brasileiro de Inspeção – SISBI',
    SUSAF_MT: 'Sistema Estadual Unificado de Sanidade Agroindustrial Familiar – SUSAF MT',
    SIAPP: 'Serviço de Inspeção Agroindustrial de Pequeno Porte – SIAPP',
  };
  return labels[sealCode] || sealCode;
};

export const productKey = (p: any) =>
  p.product_uuid ||
  p.product?.uuid ||
  `${p.product_name ?? 'NA'}-${p.measurement_unit ?? 'NA'}`;

export const sealKey = (s: any) =>
  s.uuid ||
  `${s.inspection_seal ?? 'NA'}-${s.register_number ?? 'NA'}-${s.issue_date ?? 'NA'}`;



interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

export function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

export function formatDatePtBR(dateInput?: string | null) {
  if (!dateInput) return '-';
  try {
    const d = new Date(dateInput);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('pt-BR');
  } catch (e) {
    return '-';
  }
}

export function buildReturnPayload(justificativa: string, gerenteEstadual?: string | null, now?: string) {
  const dataHora = now ?? new Date().toISOString();
  return {
    justification: justificativa,
    data_hora: dataHora,
    gerente_estadual: gerenteEstadual ?? 'Nome do Gerente Estadual',
  };
}

/** Lê campo da coleta em snake_case ou camelCase (API pode retornar em qualquer formato). */
function coletaVal(d: any, snake: string, camel?: string): string | number | null | undefined {
  if (!d) return undefined;
  const v = d[snake] ?? (camel ? d[camel] : undefined);
  if (v === null || v === undefined || v === '') return undefined;
  return typeof v === 'number' ? v : String(v).trim() || undefined;
}

export const GerenciamentoViewCicaaf: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [formData, setFormData] = useState<any | null>(null);
  const [status, setStatus] = useState<string>('');
  const [gerenteEstadual, setGerenteEstadual] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [erroJustificativa, setErroJustificativa] = useState(false);
  const { user } = useAuth();
  const isStateManager = user?.permission?.name.toUpperCase() === 'GERENTE ESTADUAL' || user?.permission?.name.toUpperCase() === 'ADMINISTRADOR';
  console.log('isStateManager', isStateManager);
  const podeDevolver = status === 'ENVIADO AO ESTADO' && isStateManager;

  useEffect(() => {
    const fetchData = async () => {
      if (!uuid) return;

      try {
        const response = await CicaafAPI.getCicaafCollection(uuid);
        if (response.Status !== 'OK') throw new Error(response.Mensagem);
        const coleta = response.dados ?? response;

        const prodResp = await CicaafAPI.getProductionComercialization(coleta.uuid);
        if (prodResp?.Status === 'OK') {
          setFormData({
            ...coleta,
            productionCommercialization: prodResp.dados,
          });
        } else {
          setFormData(coleta);
          console.warn('⚠️ Nenhuma informação de produção encontrada');
        }

        setStatus(coleta.status);
        setGerenteEstadual(coleta.created_by_user?.name || 'Nome do Gerente Estadual');
      } catch (error) {
        console.error('Erro ao buscar dados da coleta:', error);
      }
    };

    fetchData();
  }, [uuid]);



  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };



  const confirmarDevolucao = async () => {
    if (!justificativa.trim()) {
      setErroJustificativa(true);
      return;
    }
    try {
      const id = uuid ?? (() => { throw new Error('UUID não encontrado'); })();
      const dataHora = new Date().toISOString();

      const payload = {
        justification: justificativa,
        data_hora: dataHora,
        gerente_estadual: gerenteEstadual ?? 'Nome do Gerente Estadual',
      };

      const response = await CicaafAPI.returnCollectionToMunicipality(id, payload);

      if (response?.Status !== 'OK') {
        enqueueSnackbar(
          'Erro ao devolver coleta para o município: ' +
          (response?.Mensagem ?? 'Erro desconhecido'),
          {
            variant: 'error',
          },
        );
        return;
      }

      setStatus('DEVOLVIDO PELO ESTADO');
      setOpenDialog(false);
      enqueueSnackbar('Coleta devolvida com sucesso para o município!', {
        variant: 'success',
      });

      navigate(-1);
    } catch (error: any) {
      console.error('Erro ao devolver para o município:', error);
      enqueueSnackbar(
        'Erro ao conectar com o servidor. Verifique sua conexão ou tente novamente mais tarde.',
        {
          variant: 'error',
        },
      );
    }
  };

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title='Visualização Formulário CICAAF' showBackButton={true} />
      <Box
        width='97%'
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        margin='2rem'
      >
        <ContainerForm
          title='Visualização da Coleta'
          subtitle='Os dados abaixo estão em modo somente leitura'
          width='100%'
        >
          <Paper elevation={2}>
            <Tabs
              value={tabIndex}
              onChange={handleChange}
              variant='fullWidth'
              indicatorColor='primary'
              textColor='primary'
            >
              <Tab label='Identificação' />
              <Tab label='Produção' />
              <Tab label='Dados Informante' />
              <Tab label='Observações' />
            </Tabs>

            {formData && (
              <TabPanel value={tabIndex} index={0}>
                <Typography variant='h6' gutterBottom>
                  Dados de Identificação
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label='CNPJ/CPF'
                      fullWidth
                      value={mascaraCpfCnpj(formData.identification || '')}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label='Nome da Entidade'
                      fullWidth
                      value={formData.entity_name || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label='Tipo Entidade'
                      fullWidth
                      value={formData.entity_type || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid container item xs={12} spacing={2}>
                    <Grid item xs={4}>
                      {formData.statute_document && (
                        <Button
                          variant='contained'
                          color='primary'
                          fullWidth
                          startIcon={<PictureAsPdf />}
                          onClick={() => {
                            const blob = new Blob(
                              [
                                new Uint8Array(
                                  atob(formData.statute_document.base64)
                                    .split('')
                                    .map((c) => c.charCodeAt(0)),
                                ),
                              ],
                              { type: 'application/pdf' },
                            );
                            const link = document.createElement('a');
                            link.href = URL.createObjectURL(blob);
                            link.download = formData.statute_document.name;
                            link.click();
                          }}
                          sx={{ mb: 2, fontSize: '0.875rem' }}
                          title='Baixar o Estatuto Social'
                        >
                          Baixar Estatuto Social
                        </Button>
                      )}
                    </Grid>

                    <Grid item xs={4}>
                      {formData.informant_signature_file && (
                        <Button
                          variant='contained'
                          color='primary'
                          fullWidth
                          startIcon={<Image />}
                          onClick={() => {
                            const blob = new Blob(
                              [
                                new Uint8Array(
                                  atob(formData.informant_signature_file.base64)
                                    .split('')
                                    .map((c) => c.charCodeAt(0)),
                                ),
                              ],
                              { type: 'image/png' },
                            );
                            const link = document.createElement('a');
                            link.href = URL.createObjectURL(blob);
                            link.download =
                              formData.informant_signature_file.name;
                            link.click();
                          }}
                          sx={{ mb: 2, fontSize: '0.875rem' }}
                          title='Baixar Assinatura do Informante'
                        >
                          Baixar Assinatura do Informante
                        </Button>
                      )}
                    </Grid>

                    <Grid item xs={4}>
                      {formData.election_act_document && (
                        <Button
                          variant='contained'
                          color='primary'
                          fullWidth
                          startIcon={<PictureAsPdf />}
                          onClick={() => {
                            const blob = new Blob(
                              [
                                new Uint8Array(
                                  atob(formData.election_act_document.base64)
                                    .split('')
                                    .map((c) => c.charCodeAt(0)),
                                ),
                              ],
                              { type: 'application/pdf' },
                            );
                            const link = document.createElement('a');
                            link.href = URL.createObjectURL(blob);
                            link.download = formData.election_act_document.name;
                            link.click();
                          }}
                          sx={{ mb: 2, fontSize: '0.875rem' }}
                          title='Baixar Ata de Eleição'
                        >
                          Baixar Ata de Eleição
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>
            )}

            {/* ======= ABA PRODUÇÃO ======= */}
            <TabPanel value={tabIndex} index={1}>
              <Typography variant="h6" gutterBottom>
                Dados de Produção
              </Typography>

              {formData?.productionCommercialization ? (
                <>
                  {/* ==================== LISTA DE PRODUTOS ==================== */}
                  <Box mt={2}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Produtos Comercializados
                    </Typography>

                    <Grid container spacing={3}>
                      {formData.productionCommercialization.products?.map((p: any,) => (
                        <Grid item xs={12} md={6} key={productKey(p)}>


                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {p.product_name}
                          </Typography>

                          <Typography sx={{ marginBottom: '20px' }} color="text.secondary" gutterBottom>
                            {p.product_type}
                          </Typography>

                          <TextField
                            label="Valor comercializado"
                            value={Number(p.commercialized_value).toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            })}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            sx={{ mb: 2 }}
                          />

                          <TextField
                            label="Volume comercializado"
                            value={`${Number(p.commercialized_volume).toLocaleString('pt-BR')} ${p.measurement_unit || ''}`}
                            fullWidth
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* ==================== LICENÇA VEGETAL ==================== */}
                  {formData.productionCommercialization.vegetal_license && (
                    <Box mt={4}>
                      <Typography sx={{ marginBottom: '20px' }} variant="subtitle1" fontWeight="bold" gutterBottom>
                        Informações Sanitárias - Produto Vegetal
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Possui alvará sanitário"
                            value={
                              formData.productionCommercialization.vegetal_license.has_sanitary_license
                                ? 'Sim'
                                : 'Não'
                            }
                            fullWidth
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>

                        {formData.productionCommercialization.vegetal_license.has_sanitary_license && (
                          <>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Número do registro"
                                value={
                                  formData.productionCommercialization.vegetal_license.license_number || '-'
                                }
                                fullWidth
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Órgão emissor"
                                value={
                                  formData.productionCommercialization.vegetal_license.issuing_agency || '-'
                                }
                                fullWidth
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Data de emissão"
                                value={formatDatePtBR(formData.productionCommercialization.vegetal_license.issue_date)}
                                fullWidth
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {/* ==================== INSPEÇÃO ANIMAL ==================== */}
                  {formData?.productionCommercialization?.animal_inspection && (
                    <Box mt={4}>
                      <Typography sx={{ marginBottom: '20px' }} variant="subtitle1" fontWeight="bold" gutterBottom>
                        Informações Sanitárias - Produto Animal
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <TextField
                            label="Possui registro de inspeção"
                            value={
                              formData.productionCommercialization.animal_inspection.has_inspection_register
                                ? 'Sim'
                                : 'Não'
                            }
                            fullWidth
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>

                        {formData.productionCommercialization.animal_inspection.has_inspection_register &&
                          formData.productionCommercialization.animal_inspection.inspection_seals?.map(
                            (selo: any) => (
                              <Grid
                                container
                                item
                                xs={12}
                                spacing={2}
                                key={sealKey(selo)}
                                sx={{
                                  pl: 2,
                                  mt: 1,
                                }}
                              >
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="Tipo de selo"
                                    value={mapSealLabel(selo.inspection_seal)}
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="Número de registro"
                                    value={selo.register_number || '-'}
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="Data de emissão"
                                    value={formatDatePtBR(selo.issue_date)}
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                  />
                                </Grid>
                              </Grid>
                            )
                          )}
                      </Grid>
                    </Box>
                  )}


                </>
              ) : (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  Nenhuma informação de produção encontrada.
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabIndex} index={2}>
              <Typography variant='h6' gutterBottom>
                Coordenadas e Informante
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label='Latitude'
                    fullWidth
                    value={coletaVal(formData, 'latitude') ?? 'Não informado'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label='Longitude'
                    fullWidth
                    value={coletaVal(formData, 'longitude') ?? 'Não informado'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>


                <Grid item xs={12} md={6}>
                  <TextField
                    label='Nome'
                    fullWidth
                    value={coletaVal(formData, 'informant_name', 'informantName') ?? 'Não informado'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label='CPF'
                    fullWidth
                    value={mascaraCpfCnpj(String(coletaVal(formData, 'informant_document', 'informantDocument') ?? '')) || 'Não informado'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label='Email'
                    fullWidth
                    value={coletaVal(formData, 'informant_email', 'informantEmail') ?? 'Não informado'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label='Telefone'
                    fullWidth
                    type='tel'
                    value={
                      (() => {
                        const phone = coletaVal(formData, 'informant_phone', 'informantPhone');
                        return phone != null && String(phone) && /\d/.test(String(phone))
                          ? mascaraTelefone(String(phone))
                          : 'Não informado';
                      })()
                    }
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label='Titular da Entidade'
                    fullWidth
                    value={coletaVal(formData, 'president_name', 'presidentName') ?? 'Não informado'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label='Endereço'
                    fullWidth
                    value={coletaVal(formData, 'entity_address', 'entityAddress') ?? 'Não informado'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

              </Grid>
            </TabPanel>

            <TabPanel value={tabIndex} index={3}>
              <Typography variant='h6' gutterBottom>
                Observações
              </Typography>

              <TextField
                label='Observações'
                multiline
                minRows={4}
                fullWidth
                value={formData?.observations ?? ''}
                onChange={(e) =>
                  setFormData((prev: any) =>
                    prev ? { ...prev, observations: e.target.value } : prev,
                  )
                }
              />
            </TabPanel>

            {podeDevolver && (
              <Box p={3}>
                <Button
                  variant='contained'
                  color='error'
                  onClick={() => setOpenDialog(true)}
                >
                  Devolver ao Município
                </Button>
              </Box>
            )}
          </Paper>
        </ContainerForm>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth='sm'
          fullWidth
        >
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth='md'
            fullWidth
            PaperProps={{
              sx: {
                display: 'flex',
                flexDirection: 'column',
                padding: 2,
              },
            }}
          >
            <DialogTitle sx={{ fontWeight: 'bold' }}>
              Devolver Coleta ao Município
            </DialogTitle>

            <DialogContent sx={{ flex: 1 }}>
              <Typography variant='body1' color='textSecondary' sx={{ mb: 2 }}>
                Por favor, informe o motivo da devolução. Essa justificativa
                será enviada ao município e servirá de orientação para correções
                necessárias.
              </Typography>

              <TextField
                multiline
                minRows={8}
                label='Justificativa'
                placeholder='Digite sua justificativa aqui...'
                value={justificativa}
                onChange={(e) => {
                  setJustificativa(e.target.value);
                  setErroJustificativa(false);
                }}
                error={erroJustificativa}
                helperText={
                  erroJustificativa ? 'Este campo é obrigatório.' : ''
                }
                fullWidth
                variant='outlined'
              />
            </DialogContent>

            <DialogActions
              sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}
            >
              <Button
                onClick={() => setOpenDialog(false)}
                variant='outlined'
                color='inherit'
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmarDevolucao}
                variant='contained'
                color='primary'
              >
                Confirmar Devolução
              </Button>
            </DialogActions>
          </Dialog>
        </Dialog>
      </Box>
    </MenuProvider>
  );
};
